import React from "react";
import PropTypes from "prop-types";
import { Box, Flex, Icon, Notification } from "orcs-design-system";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { last } from "lodash";
import { AIMessage } from "../AIMessage";
import { AGENTCORE_NAMES } from "../consts";
import { PromptButton } from "../SuggestedPrompts";
import {
  extractFollowUpPrompts,
  stripFollowUpTags,
} from "../utils/extractFollowUpPrompts";
import MessageFeedback from "../MessageFeedback";
import GqlExpertResponse from "../GqlExpertResponse";
import HelpSources from "../HelpSources";
import { ToolBadge } from "./ToolBadge";
import { AIMessageWrapper } from "./AIMessageWrapper";

const isGqlExpertContent = (content) => {
  try {
    const parsed = JSON.parse(content);
    return !!parsed?.suggested_queries;
  } catch {
    return false;
  }
};

// Force the message body to render in the danger colour for failed builds.
const ErrorBody = styled(Box)`
  & * {
    color: ${themeGet("colors.danger")} !important;
  }
`;

const parseErrorDetails = (msg) => {
  const raw = msg?.errorDetails ?? msg?.error;
  if (!raw) {
    return null;
  }
  if (typeof raw === "object") {
    return raw;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const collectDetailLines = (details) => {
  if (!details) {
    return [];
  }
  const lines = [];
  (details.errors || []).forEach((e) => lines.push(String(e)));
  (details.diagnostics || []).forEach((d) => {
    if (d && typeof d === "object") {
      lines.push(d.message || d.code || "Issue");
    } else if (d) {
      lines.push(String(d));
    }
  });
  return lines;
};

const DiagnosticDetails = ({ details }) => {
  const lines = collectDetailLines(details);
  if (lines.length === 0) {
    return null;
  }
  return (
    <Box mt="r" as="ul" pl="r" m={0}>
      {lines.map((line, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={idx} as="li" fontSize="0" color="greyDarker" mb="2px">
          {line}
        </Box>
      ))}
    </Box>
  );
};

DiagnosticDetails.propTypes = {
  details: PropTypes.object,
};

export const AssistantMessage = ({
  message,
  messages,
  idx,
  isComplete,
  selectedAgent,
  makeHtmlParsingOptions,
  handlePromptSelect,
  onSubmitMessageFeedback,
}) => {
  const { tools = [] } = message;
  const lastTool = last(tools);
  const isLastAssistant = !messages
    .slice(idx + 1)
    .some((m) => m.role === "assistant");
  const isDone =
    message.status === "done" ||
    message.status === "failed" ||
    message.status === "error";
  const showHelpSources =
    message.status === "done" && (!isLastAssistant || isComplete);
  const followUpPrompts =
    isLastAssistant && isDone ? extractFollowUpPrompts(message.content) : [];
  const displayContent = stripFollowUpTags(message?.content);

  // Dashboard pipeline outcomes carry structured diagnostics. Failures render
  // a danger-styled block with the diagnostic lines; successful-with-warnings
  // builds get a "Built with warnings" banner under the normal response.
  // "failed" covers the Q&A fail-closed fallback (real content, not a real
  // answer) — it must render as an error too, not a normal successful reply.
  const details = parseErrorDetails(message);
  const isError = message.status === "error" || message.status === "failed";
  const errorAriaLabel =
    message.status === "failed" ? "AI response error" : "Dashboard build error";
  const hasWarnings =
    !isError &&
    (message.status === "warning" ||
      details?.kind === "pipeline_warnings" ||
      (details?.diagnostics || []).length > 0);

  if (isError) {
    return (
      <Flex
        flexDirection="column"
        justifyContent="flex-start"
        mt="4px"
        mb="4px"
        gap="8px"
      >
        <AIMessageWrapper>
          <ErrorBody>
            <AIMessage
              complete
              content={displayContent || ""}
              options={makeHtmlParsingOptions(false)}
              showAIWarning={false}
              ariaLabel={errorAriaLabel}
            />
            <DiagnosticDetails details={details} />
          </ErrorBody>
        </AIMessageWrapper>
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      mt="4px"
      mb="4px"
      gap="8px"
    >
      {lastTool && message.status !== "done" && (
        <AIMessageWrapper>
          <ToolBadge message={lastTool} allToolsCalled={tools} />
        </AIMessageWrapper>
      )}

      <AIMessageWrapper>
        {selectedAgent === AGENTCORE_NAMES.GQL_EXPERT_V2 ||
        isGqlExpertContent(displayContent) ? (
          <GqlExpertResponse content={displayContent || ""} />
        ) : (
          <AIMessage
            complete={isDone}
            content={displayContent || lastTool?.thought}
            options={makeHtmlParsingOptions(!isDone)}
            showAIWarning={false}
            ariaLabel="AI Response"
          />
        )}
        {showHelpSources && <HelpSources citations={message.citations} />}
        <Flex justifyContent="flex-end">
          <MessageFeedback
            messageId={message.id}
            initialRating={message.feedbackRating}
            initialComment={message.feedbackComment}
            onSubmitFeedback={onSubmitMessageFeedback}
            disabled={!isComplete}
          />
        </Flex>
      </AIMessageWrapper>

      {hasWarnings && (
        <AIMessageWrapper>
          <Notification
            variant="warning"
            altStyle
            closable={false}
            icon={["fas", "exclamation-triangle"]}
            aria-label="Dashboard build warnings"
          >
            <Box>
              <Box fontWeight="bold" mb="2px">
                Built with warnings
              </Box>
              <DiagnosticDetails details={details} />
            </Box>
          </Notification>
        </AIMessageWrapper>
      )}

      {isComplete && followUpPrompts.length > 0 && (
        <Flex flexWrap="wrap" gap="xs">
          {followUpPrompts.map(({ label, prompt }) => (
            <PromptButton
              key={prompt}
              variant="ghost"
              small
              disabled={!isComplete}
              onClick={() => handlePromptSelect(prompt)}
            >
              {label}
              &nbsp;&nbsp;
              {prompt.includes("/dashboard") && (
                <Icon icon={["fa", "chart-line"]} color="subText" />
              )}
            </PromptButton>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

AssistantMessage.propTypes = {
  message: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  idx: PropTypes.number.isRequired,
  isComplete: PropTypes.bool.isRequired,
  selectedAgent: PropTypes.string.isRequired,
  makeHtmlParsingOptions: PropTypes.func.isRequired,
  handlePromptSelect: PropTypes.func.isRequired,
  onSubmitMessageFeedback: PropTypes.func.isRequired,
};
